import type { ReactNode } from 'react'
import type { SessionParticipant } from '~/gql/sessions/sessions'
import { useTranslation } from 'react-i18next'
import { Badge } from '~/components/ui/badge'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '~/components/ui/table'
import { formatSeconds } from '~/helpers/learner/duration'
import { tourAnchor } from '~/helpers/tour/anchors'
import { DROPOUT_REASON_KEYS, PARTICIPANT_STATE_VARIANTS, PARTICIPANT_STATUS_KEYS, participantStatus } from './participant-state'

type IProps = {
  participants: readonly SessionParticipant[]
  formatDate: (value: unknown) => string
  /** Action de chaque ligne (abandon, #324) ; absente, la colonne n'existe pas. */
  renderAction?: (participant: SessionParticipant) => ReactNode
}

/** Une ligne par inscrit : groupe, état, avancement, score, temps, dernière activité (#243). */
function SessionParticipantsTable({ participants, formatDate, renderAction }: IProps) {
  const { t } = useTranslation()

  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>{t('common.name')}</TableHead>
          <TableHead>{t('common.groups')}</TableHead>
          <TableHead>{t('user.progress.label')}</TableHead>
          <TableHead>{t('sessionDetail.participants.modules')}</TableHead>
          <TableHead>{t('common.score')}</TableHead>
          <TableHead>{t('common.totalTime')}</TableHead>
          <TableHead>{t('sessionDetail.participants.lastActivity')}</TableHead>
          {renderAction && <TableHead {...tourAnchor('sessionDetail.dropout')}><span className="sr-only">{t('sessionDetail.participants.actions')}</span></TableHead>}
        </TableRow>
      </TableHeader>
      <TableBody>
        {participants.map(p => (
          <TableRow key={p.userId}>
            <TableCell>
              <div className="flex flex-col">
                <span className="font-medium">{[p.firstName, p.lastName].filter(Boolean).join(' ') || p.email}</span>
                <span className="text-xs text-muted-foreground">{p.email}</span>
              </div>
            </TableCell>
            <TableCell>{p.groups.join(', ') || '—'}</TableCell>
            <TableCell>
              <Badge variant={PARTICIPANT_STATE_VARIANTS[participantStatus(p)]}>{t(PARTICIPANT_STATUS_KEYS[participantStatus(p)])}</Badge>
              {p.dropReason && (
                <p className="mt-1 text-xs text-muted-foreground" title={p.dropNote ?? undefined}>
                  {t(DROPOUT_REASON_KEYS[p.dropReason])}
                  {p.droppedAt ? ` · ${formatDate(p.droppedAt)}` : ''}
                </p>
              )}
            </TableCell>
            <TableCell className="tabular-nums">{`${p.completedModules}/${p.totalModules}`}</TableCell>
            <TableCell className="tabular-nums">{p.averageScorePercent != null ? `${p.averageScorePercent} %` : '—'}</TableCell>
            <TableCell className="tabular-nums">{formatSeconds(p.timeSpentSeconds, t) ?? '—'}</TableCell>
            <TableCell>{p.lastActivityAt ? formatDate(p.lastActivityAt) : '—'}</TableCell>
            {renderAction && <TableCell className="text-right">{renderAction(p)}</TableCell>}
          </TableRow>
        ))}
      </TableBody>
    </Table>
  )
}

export default SessionParticipantsTable
